import React, { useMemo } from 'react';
import { OpportunityTile } from '../../../../../../useProductAnalysis.logic';
import './ContractTypesSection.styles.scss';

interface ContractTypesSectionProps {
  contractTypesTile: OpportunityTile | null;
}

interface ContractType {
  type: string;
  count: string;
}

const ContractTypesSection: React.FC<ContractTypesSectionProps> = ({ contractTypesTile }) => {
  // Parse contract types from tile value
  // Expected format: "type1: count1, type2: count2, ..." or "No contracts"
  const contractTypes = useMemo(() => {
    if (!contractTypesTile || !contractTypesTile.value) {
      return [];
    }

    const value = contractTypesTile.value.trim();
    
    // Check if it's "No contracts"
    if (value.toLowerCase() === 'no contracts') {
      return [];
    }

    // Parse the contract types string
    // Format: "type1: count1, type2: count2, ..."
    const types: ContractType[] = [];
    const parts = value.split(',').map(part => part.trim()).filter(part => part.length > 0);

    parts.forEach(part => {
      // Split by colon to get type and count
      const colonIndex = part.indexOf(':');
      if (colonIndex > 0) {
        const type = part.substring(0, colonIndex).trim();
        const count = part.substring(colonIndex + 1).trim();
        if (type && count) {
          types.push({ type, count });
        }
      } else {
        // If no colon, treat entire part as type with empty count
        types.push({ type: part, count: '' });
      }
    });

    return types;
  }, [contractTypesTile]);

  // Don't render if no contract types data
  if (!contractTypesTile || contractTypes.length === 0) {
    return null;
  }

  return (
    <div className="contract-types-section">
      <div className="contract-types-header">
        <h3 className="contract-types-title">Contract Types</h3>
      </div>
      <div className="contract-types-table">
        {contractTypes.map((contractType, index) => (
          <div key={index} className="contract-type-row">
            <span className="contract-type-name">{contractType.type}</span>
            <span className="contract-type-separator">:</span>
            <span className="contract-type-count">{contractType.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContractTypesSection;
